import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, TrendingUp, TrendingDown, Star, StarOff, Bell, BellPlus,
  ExternalLink, RefreshCw, ChevronDown, ChevronUp, Info, Zap, Search, Brain,
  Building2, Landmark, Users, FileText, BarChart3, PieChart, LineChart,
  Calendar, Clock, DollarSign, Percent, Activity, Target, AlertTriangle,
  ChevronRight, MessageSquare, Share2, Download, HelpCircle,
  X, GitCompare, Play, Pause, ChevronLeft, Filter, Minus, Mail, Phone, Send, Maximize2
} from 'lucide-react';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import './StockPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// =============================================================================
// STOCK DATA SERVICE - Ready for real API integration
// =============================================================================
const StockDataService = {
  async getQuote(symbol) {
    try {
      const response = await fetch(`${API_URL}/api/stocks/quote/${symbol}`);
      if (!response.ok) throw new Error('Failed to fetch quote');
      return await response.json();
    } catch (error) { console.error('Error fetching quote:', error); return null; }
  },
  async getProfile(symbol) {
    try {
      const response = await fetch(`${API_URL}/api/stocks/profile/${symbol}`);
      if (!response.ok) throw new Error('Failed to fetch profile');
      return await response.json();
    } catch (error) { console.error('Error fetching profile:', error); return null; }
  },
  async getFinancials(symbol, statement = 'income', period = 'annual') {
    try {
      const response = await fetch(`${API_URL}/api/stocks/financials/${symbol}?statement=${statement}&period=${period}`);
      if (!response.ok) throw new Error('Failed to fetch financials');
      return await response.json();
    } catch (error) { console.error('Error fetching financials:', error); return null; }
  },
  async getAnalystData(symbol) {
    try {
      const response = await fetch(`${API_URL}/api/stocks/analysts/${symbol}`);
      if (!response.ok) throw new Error('Failed to fetch analyst data');
      return await response.json();
    } catch (error) { console.error('Error fetching analyst data:', error); return null; }
  },
  async getInsiderTrading(symbol) {
    try {
      const response = await fetch(`${API_URL}/api/stocks/insiders/${symbol}`);
      if (!response.ok) throw new Error('Failed to fetch insider trading');
      return await response.json();
    } catch (error) { console.error('Error fetching insider trading:', error); return null; }
  },
  async getInstitutionalOwnership(symbol) {
    try {
      const response = await fetch(`${API_URL}/api/stocks/institutions/${symbol}`);
      if (!response.ok) throw new Error('Failed to fetch institutional data');
      return await response.json();
    } catch (error) { console.error('Error fetching institutional data:', error); return null; }
  },
  async getCongressTrading(symbol) {
    try {
      const response = await fetch(`${API_URL}/api/stocks/congress/${symbol}`);
      if (!response.ok) throw new Error('Failed to fetch congress trading');
      return await response.json();
    } catch (error) { console.error('Error fetching congress trading:', error); return null; }
  },
  async getGovernmentContracts(symbol) {
    try {
      const response = await fetch(`${API_URL}/api/stocks/contracts/${symbol}`);
      if (!response.ok) throw new Error('Failed to fetch contracts');
      return await response.json();
    } catch (error) { console.error('Error fetching contracts:', error); return null; }
  },
  async getLobbyingData(symbol) {
    try {
      const response = await fetch(`${API_URL}/api/stocks/lobbying/${symbol}`);
      if (!response.ok) throw new Error('Failed to fetch lobbying data');
      return await response.json();
    } catch (error) { console.error('Error fetching lobbying data:', error); return null; }
  },
  async getNews(symbol) {
    try {
      const response = await fetch(`${API_URL}/api/stocks/news/${symbol}`);
      if (!response.ok) throw new Error('Failed to fetch news');
      return await response.json();
    } catch (error) { console.error('Error fetching news:', error); return null; }
  },
  async getAISummary(symbol, context = {}) {
    try {
      const response = await fetch(`${API_URL}/api/ai/stock-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol, context })
      });
      if (!response.ok) throw new Error('Failed to generate AI summary');
      return await response.json();
    } catch (error) { console.error('Error generating AI summary:', error); return null; }
  }
};

// =============================================================================
// MOCK DATA - Will be replaced with real API data
// =============================================================================
const getMockData = (symbol) => {
  const mockStocks = {
    MSFT: {
      quote: {
        symbol: 'MSFT', name: 'Microsoft Corporation', price: 430.29, change: -4.80, changePercent: -1.11,
        open: 435.00, high: 436.50, low: 428.15, previousClose: 435.09, volume: 18420000, avgVolume: 20150000,
        marketCap: 3200000000000, pe: 36.5, eps: 11.79, beta: 0.89, week52High: 468.35, week52Low: 309.45,
        dividend: 3.00, dividendYield: 0.70, exDividendDate: '2024-02-14', earningsDate: '2024-04-25',
        lastUpdated: new Date().toISOString()
      },
      profile: {
        symbol: 'MSFT', name: 'Microsoft Corporation',
        description: 'Microsoft develops and licenses consumer and enterprise software. It is known for its Windows operating systems and Office productivity suite. The company is organized into three broad segments: productivity and business processes (legacy Microsoft Office, cloud-based Office 365, Exchange, SharePoint, Skype, LinkedIn, Dynamics), intelligence cloud (infrastructure- and platform-as-a-service offerings Azure, Windows Server OS, SQL Server), and more personal computing (Windows Client, Xbox, Bing search, display advertising, and Surface laptops, tablets, and desktops).',
        sector: 'Technology', industry: 'Software - Infrastructure', ceo: 'Satya Nadella',
        employees: 228000, headquarters: 'Redmond, WA', founded: 1975,
        website: 'https://www.microsoft.com', logo: 'https://logo.clearbit.com/microsoft.com'
      },
      keyStats: {
        marketCap: 3200000000000, enterpriseValue: 3180000000000, trailingPE: 36.5, forwardPE: 32.1,
        pegRatio: 2.45, priceToSales: 13.2, priceToBook: 12.8, profitMargin: 0.362, operatingMargin: 0.445,
        returnOnEquity: 0.385, returnOnAssets: 0.147, revenueGrowth: 0.167, earningsGrowth: 0.21,
        debtToEquity: 0.35, currentRatio: 1.77, quickRatio: 1.73
      }
    },
    NVDA: {
      quote: {
        symbol: 'NVDA', name: 'NVIDIA Corporation', price: 142.50, change: 5.25, changePercent: 3.82,
        open: 138.00, high: 144.20, low: 137.50, previousClose: 137.25, volume: 45200000, avgVolume: 42000000,
        marketCap: 3500000000000, pe: 65.2, eps: 2.19, beta: 1.72, week52High: 152.89, week52Low: 47.32,
        dividend: 0.16, dividendYield: 0.03, exDividendDate: '2024-03-05', earningsDate: '2024-02-21',
        lastUpdated: new Date().toISOString()
      },
      profile: {
        symbol: 'NVDA', name: 'NVIDIA Corporation',
        description: 'NVIDIA Corporation provides graphics and compute and networking solutions. The company operates through Graphics and Compute & Networking segments. It offers GPUs for gaming, professional visualization, data center, and automotive markets. NVIDIA is a leader in AI computing hardware.',
        sector: 'Technology', industry: 'Semiconductors', ceo: 'Jensen Huang',
        employees: 29600, headquarters: 'Santa Clara, CA', founded: 1993,
        website: 'https://www.nvidia.com', logo: 'https://logo.clearbit.com/nvidia.com'
      },
      keyStats: {
        marketCap: 3500000000000, enterpriseValue: 3450000000000, trailingPE: 65.2, forwardPE: 42.5,
        pegRatio: 1.15, priceToSales: 32.5, priceToBook: 52.3, profitMargin: 0.552, operatingMargin: 0.621,
        returnOnEquity: 0.915, returnOnAssets: 0.453, revenueGrowth: 1.22, earningsGrowth: 2.65,
        debtToEquity: 0.41, currentRatio: 4.17, quickRatio: 3.52
      }
    }
  };

  const defaultMock = {
    quote: {
      symbol: symbol, name: `${symbol} Inc.`, price: 100 + Math.random() * 200,
      change: (Math.random() - 0.5) * 10, changePercent: (Math.random() - 0.5) * 5,
      open: 100 + Math.random() * 200, high: 110 + Math.random() * 200, low: 90 + Math.random() * 200,
      previousClose: 100 + Math.random() * 200, volume: Math.floor(Math.random() * 50000000),
      avgVolume: Math.floor(Math.random() * 40000000), marketCap: Math.floor(Math.random() * 1000000000000),
      pe: 15 + Math.random() * 30, eps: Math.random() * 10, beta: 0.5 + Math.random() * 1.5,
      week52High: 150 + Math.random() * 100, week52Low: 50 + Math.random() * 50,
      lastUpdated: new Date().toISOString()
    },
    profile: {
      symbol: symbol, name: `${symbol} Inc.`, description: `${symbol} is a publicly traded company.`,
      sector: 'Technology', industry: 'Software', ceo: 'John Smith', employees: 10000,
      headquarters: 'San Francisco, CA', founded: 2000, website: `https://www.${symbol.toLowerCase()}.com`
    },
    keyStats: {
      marketCap: Math.floor(Math.random() * 1000000000000), trailingPE: 15 + Math.random() * 30,
      forwardPE: 12 + Math.random() * 25, profitMargin: Math.random() * 0.4, returnOnEquity: Math.random() * 0.5
    }
  };

  return mockStocks[symbol] || defaultMock;
};

const getMockFinancials = (symbol) => ({
  income: {
    annual: [
      { period: 'Q2 2026', endDate: 'Dec 31, 2025', revenue: 81.27e9, revenueGrowth: 0.1672, costOfRevenue: 25.98e9, grossProfit: 55.3e9, operatingExpenses: 17.02e9, operatingIncome: 38.28e9, netIncome: 48.25e9, eps: 6.48 },
      { period: 'Q1 2026', endDate: 'Sep 30, 2025', revenue: 77.67e9, revenueGrowth: 0.1843, costOfRevenue: 24.04e9, grossProfit: 53.63e9, operatingExpenses: 15.67e9, operatingIncome: 37.96e9, netIncome: 34.3e9, eps: 4.60 },
      { period: 'Q4 2025', endDate: 'Jun 30, 2025', revenue: 281.72e9, revenueGrowth: 0.1493, costOfRevenue: 87.83e9, grossProfit: 193.89e9, operatingExpenses: 65.37e9, operatingIncome: 128.53e9, netIncome: 123.63e9, eps: 16.58 },
      { period: 'Q3 2025', endDate: 'Mar 31, 2025', revenue: 70.07e9, revenueGrowth: 0.1327, costOfRevenue: 21.92e9, grossProfit: 48.15e9, operatingExpenses: 16.15e9, operatingIncome: 32e9, netIncome: 31.38e9, eps: 4.21 },
    ],
    quarterly: []
  },
  balance: { annual: [{ period: 'Q2 2026', totalAssets: 512e9, totalLiabilities: 198e9, totalEquity: 314e9, cash: 75e9, debt: 58e9, currentAssets: 142e9, currentLiabilities: 95e9 }] },
  cashflow: { annual: [{ period: 'Q2 2026', operatingCashFlow: 42e9, investingCashFlow: -15e9, financingCashFlow: -22e9, freeCashFlow: 38e9, capex: 4e9 }] },
  revenueBreakdown: {
    bySegment: [
      { segment: 'Productivity and Business Processes', revenue: 33.1e9, percentage: 43.32 },
      { segment: 'Intelligent Cloud', revenue: 29.9e9, percentage: 39.09 },
      { segment: 'More Personal Computing', revenue: 13.5e9, percentage: 17.60 }
    ],
    byGeography: [
      { region: 'United States', revenue: 45e9, percentage: 55 },
      { region: 'Europe', revenue: 20e9, percentage: 25 },
      { region: 'Asia Pacific', revenue: 12e9, percentage: 15 },
      { region: 'Other', revenue: 4e9, percentage: 5 }
    ]
  }
});

const getMockAnalystData = (symbol) => ({
  ratings: { buy: 20, hold: 1, sell: 0, consensus: 'Buy', totalAnalysts: 21 },
  priceTargets: { high: 700.00, median: 625.00, low: 500.00, current: 430.29, upside: 45.25 },
  forecasts: [
    { analyst: 'Tyler Radke', firm: 'Citigroup', rating: 'Buy', priceTarget: 635.00, previousTarget: 660.00, date: 'Jan 30, 2026' },
    { analyst: 'Gil Luria', firm: 'DA Davidson', rating: 'Buy', priceTarget: 650.00, previousTarget: 650.00, date: 'Jan 29, 2026' },
    { analyst: 'Michael Turrin', firm: 'Wells Fargo', rating: 'Overweight', priceTarget: 615.00, previousTarget: 630.00, date: 'Jan 29, 2026' },
    { analyst: 'Hannah Rudoff', firm: 'Piper Sandler', rating: 'Overweight', priceTarget: 600.00, previousTarget: 650.00, date: 'Jan 29, 2026' },
    { analyst: 'Kirk Materne', firm: 'Evercore ISI Group', rating: 'Outperform', priceTarget: 580.00, previousTarget: 640.00, date: 'Jan 29, 2026' },
  ]
});

const getMockInsiderData = (symbol) => ({
  summary: { netShares: -125000, netValue: -5200000, buyCount: 2, sellCount: 8, period: '6 months' },
  transactions: [
    { name: 'Numoto Takeshi', title: 'EVP, Chief Marketing Officer', type: 'Sale', shares: 2850, price: 478.72, value: 1364352, sharesHeld: 55781, date: 'Dec 04, 2025', reported: 'Dec 5, 2025' },
    { name: 'Althoff Judson', title: 'CEO Microsoft Commercial', type: 'Sale', shares: 12750, price: 491.52, value: 6266880, sharesHeld: 129348, date: 'Dec 02, 2025', reported: 'Dec 3, 2025' },
    { name: 'Smith Bradford L', title: 'Vice Chair & President', type: 'Sale', shares: 30411, price: 518.49, value: 15768437, sharesHeld: 469685, date: 'Nov 03, 2025', reported: 'Nov 4, 2025' },
    { name: 'Hood Amy', title: 'EVP & CFO', type: 'Sale', shares: 15000, price: 425.30, value: 6379500, sharesHeld: 234521, date: 'Oct 15, 2025', reported: 'Oct 16, 2025' },
    { name: 'Nadella Satya', title: 'Chairman & CEO', type: 'Sale', shares: 10000, price: 412.80, value: 4128000, sharesHeld: 892456, date: 'Sep 20, 2025', reported: 'Sep 21, 2025' },
  ]
});

const getMockInstitutionalData = (symbol) => ({
  summary: { totalInstitutions: 4521, totalShares: 5824000000, percentHeld: 72.5, increased: 1114, decreased: 1095, held: 167 },
  topHolders: [
    { name: 'VANGUARD GROUP INC', shares: 717942580, value: 347.21e9, change: 15955898, changePercent: 2.27, date: 'Dec 31, 2025', type: 'Institution' },
    { name: 'BlackRock, Inc.', shares: 591860058, value: 306.55e9, change: 10283002, changePercent: 1.77, date: 'Sep 30, 2025', type: 'Institution' },
    { name: 'STATE STREET CORP', shares: 299761678, value: 155.26e9, change: 565159, changePercent: 0.19, date: 'Sep 30, 2025', type: 'Institution' },
    { name: 'FMR LLC', shares: 210487489, value: 109.05e9, change: -8234521, changePercent: -3.76, date: 'Sep 30, 2025', type: 'Institution' },
    { name: 'GEODE CAPITAL MANAGEMENT, LLC', shares: 180707258, value: 93.63e9, change: 4521365, changePercent: 2.56, date: 'Sep 30, 2025', type: 'Institution' },
    { name: 'JPMORGAN CHASE & CO', shares: 158807898, value: 82.28e9, change: 2145632, changePercent: 1.37, date: 'Sep 30, 2025', type: 'Institution' },
    { name: 'PRICE T ROWE ASSOCIATES INC', shares: 131514660, value: 68.13e9, change: -5632145, changePercent: -4.11, date: 'Sep 30, 2025', type: 'Institution' },
    { name: 'MORGAN STANLEY', shares: 120240122, value: 62.29e9, change: 1254632, changePercent: 1.05, date: 'Sep 30, 2025', type: 'Institution' },
  ],
  etfHolders: [
    { name: '465580', fullName: 'KIM ACE US Big Tech TOP 7 Plus ETF Units', holdings: 138e9 },
    { name: 'VTI', fullName: 'Vanguard Total Stock Market ETF', holdings: 113e9 },
    { name: 'VOO', fullName: 'Vanguard S&P 500 ETF', holdings: 91e9 },
    { name: 'IVV', fullName: 'iShares Core S&P 500 ETF', holdings: 41e9 },
    { name: 'SPY', fullName: 'SPDR S&P 500 ETF Trust', holdings: 39e9 },
  ]
});

const getMockCongressData = (symbol) => ({
  summary: { totalTrades: 12, buyCount: 8, sellCount: 4, totalValue: 850000, period: '12 months' },
  trades: [
    { politician: 'David Taylor', chamber: 'House', party: 'R', type: 'Purchase', amount: '$1,001 - $15,000', date: 'Jan 08, 2026' },
    { politician: 'David Taylor', chamber: 'House', party: 'R', type: 'Purchase', amount: '$1,001 - $15,000', date: 'Jan 08, 2026' },
    { politician: 'Markwayne Mullin', chamber: 'Senate', party: 'R', type: 'Purchase', amount: '$100,001 - $250,000', date: 'Dec 29, 2025' },
    { politician: 'Gilbert Ray Cisneros', chamber: 'House', party: 'D', type: 'Sale', amount: '$15,001 - $50,000', date: 'Dec 19, 2025' },
    { politician: 'Julie Johnson', chamber: 'House', party: 'D', type: 'Sale', amount: '$1,001 - $15,000', date: 'Dec 18, 2025' },
  ],
  lobbying: [
    { amount: 2420000, date: 'Jan 20, 2026', issues: 'Education Immigration Science/Technology Taxation/Internal Revenue Code Computer Industry Copyright/Patent/Trademark Consumer Issues/Safety/Products Telecommunications Trade Environment Defense Transportation' },
    { amount: 2020000, date: 'Oct 20, 2025', issues: 'Science/Technology Computer Industry Copyright/Patent/Trademark Consumer Issues Education Immigration Taxation Internal Revenue Code Telecommunications Trade Environment Defense Transportation' },
  ],
  contracts: [
    { amount: 2207391, date: '2026-01-16', agency: 'Department of Transportation' },
    { amount: 1798305, date: '2025-12-23', agency: 'Social Security Administration' },
    { amount: 321596, date: '2025-12-18', agency: 'Department of Defense' },
  ]
});

const getMockNews = (symbol) => ([
  { id: 1, title: `New Buy Rating for ${symbol}, the Technology Giant`, source: 'TheStreet', time: '2h ago', sentiment: 'bullish', image: null },
  { id: 2, title: `$${symbol} stock fell 8% this week. Here's what we see in our data.`, source: 'Quiver Quantitative', time: '1d ago', sentiment: 'bearish', image: null },
  { id: 3, title: `Fund Update: NEW YORK STATE TEACHERS RETIREMENT SYSTEM Just Disclosed New Holdings`, source: 'Quiver Quantitative', time: '2d ago', sentiment: 'neutral', image: null },
  { id: 4, title: `${symbol} announces expanded AI partnership with OpenAI`, source: 'Reuters', time: '3d ago', sentiment: 'bullish', image: null },
  { id: 5, title: `Analyst raises price target on ${symbol} ahead of earnings`, source: 'Bloomberg', time: '4d ago', sentiment: 'bullish', image: null },
]);

// =============================================================================
// UTILITY COMPONENTS
// =============================================================================
const LoadingSpinner = () => (
  <div className="loading-spinner">
    <RefreshCw className="spinning" size={24} />
    <span>Loading...</span>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="error-state">
    <AlertTriangle size={32} />
    <p>{message}</p>
    {onRetry && <button onClick={onRetry} className="btn-retry">Try Again</button>}
  </div>
);

const formatNumber = (num, options = {}) => {
  if (num === null || num === undefined) return '—';
  const { decimals = 2, prefix = '', suffix = '', compact = false } = options;
  if (compact) {
    if (Math.abs(num) >= 1e12) return `${prefix}${(num / 1e12).toFixed(2)}T${suffix}`;
    if (Math.abs(num) >= 1e9) return `${prefix}${(num / 1e9).toFixed(2)}B${suffix}`;
    if (Math.abs(num) >= 1e6) return `${prefix}${(num / 1e6).toFixed(2)}M${suffix}`;
    if (Math.abs(num) >= 1e3) return `${prefix}${(num / 1e3).toFixed(2)}K${suffix}`;
  }
  return `${prefix}${num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`;
};

const formatPercent = (num) => {
  if (num === null || num === undefined) return '—';
  const sign = num >= 0 ? '+' : '';
  return `${sign}${(num * 100).toFixed(2)}%`;
};

const formatCurrency = (num, compact = true) => {
  return formatNumber(num, { prefix: '$', compact });
};

// =============================================================================
// AI INSIGHT CARD - "Ask more" opens the site-wide AIChatWidget
// =============================================================================
function AIInsightCard({ title, insight, onAskMore, isLoading }) {
  return (
    <div className="ai-insight-card">
      <div className="ai-insight-header">
        <div className="ai-icon">
          <Brain size={16} />
        </div>
        <h4>{title || 'AI Insight'}</h4>
        {onAskMore && (
          <button className="ask-more-btn" onClick={onAskMore}>
            <MessageSquare size={12} />
            Ask more
          </button>
        )}
      </div>
      <div className="ai-insight-content">
        {isLoading ? (
          <div className="ai-loading">
            <span className="typing-indicator">
              <span></span><span></span><span></span>
            </span>
            Analyzing...
          </div>
        ) : (
          <p>{insight}</p>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// ALERT MODAL COMPONENT
// =============================================================================
function AlertModal({ symbol, currentPrice, onClose, onSave }) {
  const [alertType, setAlertType] = useState('price_above');
  const [alertValue, setAlertValue] = useState(currentPrice ? (currentPrice * 1.05).toFixed(2) : '');
  const [alertName, setAlertName] = useState(`${symbol} Price Alert`);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyPush, setNotifyPush] = useState(true);

  const alertTypes = [
    { id: 'price_above', label: 'Price goes above', icon: TrendingUp },
    { id: 'price_below', label: 'Price goes below', icon: TrendingDown },
    { id: 'percent_change_up', label: 'Increases by %', icon: TrendingUp },
    { id: 'percent_change_down', label: 'Decreases by %', icon: TrendingDown },
  ];

  const handleSave = () => {
    const alert = {
      id: `alert_${Date.now()}`, symbol: symbol.toUpperCase(), type: alertType,
      value: parseFloat(alertValue), name: alertName, notifyEmail, notifyPush,
      currentPrice, createdAt: new Date().toISOString(), triggered: false
    };
    const existingAlerts = JSON.parse(localStorage.getItem('stockAlerts') || '[]');
    existingAlerts.push(alert);
    localStorage.setItem('stockAlerts', JSON.stringify(existingAlerts));
    if (onSave) onSave(alert);
    onClose();
  };

  return (
    <div className="alert-modal-overlay" onClick={onClose}>
      <div className="alert-modal" onClick={e => e.stopPropagation()}>
        <div className="alert-modal-header">
          <h2><Bell size={20} /> Set Price Alert for {symbol}</h2>
          <button className="alert-modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="alert-modal-body">
          <div className="alert-field">
            <label>Alert Name</label>
            <input type="text" value={alertName} onChange={(e) => setAlertName(e.target.value)} placeholder="My Price Alert" />
          </div>
          <div className="alert-field">
            <label>Alert Condition</label>
            <div className="alert-type-grid">
              {alertTypes.map(type => (
                <button key={type.id} className={`alert-type-btn ${alertType === type.id ? 'active' : ''}`} onClick={() => setAlertType(type.id)}>
                  <type.icon size={16} />{type.label}
                </button>
              ))}
            </div>
          </div>
          <div className="alert-field">
            <label>{alertType.includes('percent') ? 'Percentage (%)' : 'Target Price ($)'}</label>
            <div className="alert-value-input">
              <span className="alert-value-prefix">{alertType.includes('percent') ? '%' : '$'}</span>
              <input type="number" value={alertValue} onChange={(e) => setAlertValue(e.target.value)} placeholder={alertType.includes('percent') ? '5' : currentPrice?.toFixed(2)} step={alertType.includes('percent') ? '0.5' : '0.01'} />
            </div>
            {currentPrice && !alertType.includes('percent') && (
              <div className="alert-price-hint">
                Current price: ${currentPrice.toFixed(2)}
                {alertValue && (
                  <span className={parseFloat(alertValue) > currentPrice ? 'positive' : 'negative'}>
                    {' '}({((parseFloat(alertValue) - currentPrice) / currentPrice * 100).toFixed(2)}% {parseFloat(alertValue) > currentPrice ? 'above' : 'below'})
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="alert-field">
            <label>Notify me via</label>
            <div className="alert-notify-options">
              <label className="alert-checkbox"><input type="checkbox" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} /><span className="checkmark"></span>Email</label>
              <label className="alert-checkbox"><input type="checkbox" checked={notifyPush} onChange={(e) => setNotifyPush(e.target.checked)} /><span className="checkmark"></span>Push notification</label>
            </div>
          </div>
        </div>
        <div className="alert-modal-footer">
          <button className="alert-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="alert-btn-save" onClick={handleSave} disabled={!alertValue || !alertName}><Bell size={16} /> Create Alert</button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// PRICE HEADER
// =============================================================================
function PriceHeader({ quote, profile, onAddToWatchlist, onSetAlert, onCompare, isWatchlisted }) {
  const isPositive = quote?.change >= 0;
  return (
    <div className="price-header">
      <div className="price-header-left">
        {profile?.logo && (<img src={profile.logo} alt={profile.name} className="company-logo" onError={(e) => e.target.style.display = 'none'} />)}
        <div className="price-info">
          <div className="company-name-row">
            <h1>{profile?.name || quote?.symbol}</h1>
            <span className="symbol-badge">{quote?.symbol}</span>
            {profile?.sector && <span className="sector-badge">{profile.sector}</span>}
          </div>
          <div className="price-row">
            <span className="current-price">{formatCurrency(quote?.price, false)}</span>
            <span className={`price-change ${isPositive ? 'positive' : 'negative'}`}>
              {isPositive ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              {formatNumber(quote?.change, { decimals: 2, prefix: isPositive ? '+' : '' })}
              ({formatNumber(quote?.changePercent, { decimals: 2, prefix: isPositive ? '+' : '', suffix: '%' })})
            </span>
          </div>
          <div className="last-updated">As of {new Date(quote?.lastUpdated).toLocaleString()} • {quote?.marketCap ? 'Market Open' : 'Market Closed'}</div>
        </div>
      </div>
      <div className="price-header-actions">
        <button className={`action-btn ${isWatchlisted ? 'active' : ''}`} onClick={onAddToWatchlist}>
          {isWatchlisted ? <Star size={18} fill="currentColor" /> : <StarOff size={18} />}
          {isWatchlisted ? 'Watching' : 'Add to Watchlist'}
        </button>
        <button className="action-btn" onClick={onSetAlert}><BellPlus size={18} /> Set Alert</button>
        <button className="action-btn" onClick={onCompare}><GitCompare size={18} /> Compare</button>
      </div>
    </div>
  );
}

// =============================================================================
// TAB NAVIGATION
// =============================================================================
function TabNavigation({ activeTab, onTabChange, tabs }) {
  return (
    <nav className="tab-navigation">
      {tabs.map(tab => (
        <button key={tab.id} className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`} onClick={() => onTabChange(tab.id)}>
          {tab.icon && <tab.icon size={16} />}{tab.label}
        </button>
      ))}
    </nav>
  );
}

// =============================================================================
// OVERVIEW TAB
// =============================================================================
function OverviewTab({ symbol, quote, profile, keyStats, onOpenAI }) {
  const aiSummary = `${profile?.name || symbol} is currently trading at ${formatCurrency(quote?.price, false)}, ${quote?.changePercent >= 0 ? 'up' : 'down'} ${Math.abs(quote?.changePercent).toFixed(2)}% today. The stock has a P/E ratio of ${quote?.pe?.toFixed(1) || 'N/A'} and market cap of ${formatCurrency(quote?.marketCap)}. Recent analyst consensus is bullish with strong institutional support from Vanguard and BlackRock.`;

  return (
    <div className="tab-content overview-tab">
      <AIInsightCard title="AI Summary" insight={aiSummary} onAskMore={() => onOpenAI?.(`Tell me more about ${symbol}'s current situation`)} />
      <div className="stats-section">
        <h3>Key Statistics</h3>
        <div className="stats-grid">
          <div className="stat-card"><span className="stat-label">Market Cap</span><span className="stat-value">{formatCurrency(quote?.marketCap)}</span></div>
          <div className="stat-card"><span className="stat-label">P/E Ratio (TTM)</span><span className="stat-value">{formatNumber(keyStats?.trailingPE || quote?.pe, { decimals: 2 })}</span></div>
          <div className="stat-card"><span className="stat-label">P/E Ratio (FWD)</span><span className="stat-value">{formatNumber(keyStats?.forwardPE, { decimals: 2 })}</span></div>
          <div className="stat-card"><span className="stat-label">EPS (TTM)</span><span className="stat-value">{formatCurrency(quote?.eps, false)}</span></div>
          <div className="stat-card"><span className="stat-label">Dividend Yield</span><span className="stat-value">{formatNumber(quote?.dividendYield, { decimals: 2, suffix: '%' })}</span></div>
          <div className="stat-card"><span className="stat-label">Beta</span><span className="stat-value">{formatNumber(quote?.beta, { decimals: 2 })}</span></div>
          <div className="stat-card"><span className="stat-label">52W High</span><span className="stat-value">{formatCurrency(quote?.week52High, false)}</span></div>
          <div className="stat-card"><span className="stat-label">52W Low</span><span className="stat-value">{formatCurrency(quote?.week52Low, false)}</span></div>
          <div className="stat-card"><span className="stat-label">Avg Volume</span><span className="stat-value">{formatNumber(quote?.avgVolume, { compact: true })}</span></div>
          <div className="stat-card"><span className="stat-label">Volume</span><span className="stat-value">{formatNumber(quote?.volume, { compact: true })}</span></div>
          <div className="stat-card"><span className="stat-label">Open</span><span className="stat-value">{formatCurrency(quote?.open, false)}</span></div>
          <div className="stat-card"><span className="stat-label">Previous Close</span><span className="stat-value">{formatCurrency(quote?.previousClose, false)}</span></div>
        </div>
      </div>
      <div className="about-section">
        <h3>About {profile?.name}</h3>
        <p className="company-description">{profile?.description}</p>
        <div className="company-details-grid">
          <div className="detail-item"><span className="detail-label">Headquarters</span><span className="detail-value">{profile?.headquarters}</span></div>
          <div className="detail-item"><span className="detail-label">CEO</span><span className="detail-value">{profile?.ceo}</span></div>
          <div className="detail-item"><span className="detail-label">Employees</span><span className="detail-value">{formatNumber(profile?.employees, { decimals: 0 })}</span></div>
          <div className="detail-item"><span className="detail-label">Industry</span><span className="detail-value">{profile?.industry}</span></div>
          <div className="detail-item"><span className="detail-label">Founded</span><span className="detail-value">{profile?.founded}</span></div>
          <div className="detail-item"><span className="detail-label">Website</span><a href={profile?.website} target="_blank" rel="noopener noreferrer" className="detail-link">{profile?.website?.replace('https://', '')} <ExternalLink size={12} /></a></div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// FINANCIALS TAB
// =============================================================================
function FinancialsTab({ symbol, financials, onOpenAI }) {
  const [activeStatement, setActiveStatement] = useState('income');
  const [activePeriod, setActivePeriod] = useState('annual');
  const [activeBreakdown, setActiveBreakdown] = useState('segment');
  const statements = [{ id: 'income', label: 'Income' },{ id: 'balance', label: 'Balance Sheet' },{ id: 'cashflow', label: 'Cash Flow' },{ id: 'breakdown', label: 'Revenue Breakdown' }];
  const aiInsight = `Revenue growth of 16.7% YoY indicates strong demand for cloud services. Intelligent Cloud segment continues to drive growth at 39% of revenue. Operating margins remain healthy at 45%, though R&D expenses are increasing to support AI initiatives.`;

  return (
    <div className="tab-content financials-tab">
      <AIInsightCard title="Financial Analysis" insight={aiInsight} onAskMore={() => onOpenAI?.(`Analyze ${symbol}'s financial statements in detail`)} />
      <div className="statement-tabs">
        {statements.map(stmt => (<button key={stmt.id} className={`statement-tab ${activeStatement === stmt.id ? 'active' : ''}`} onClick={() => setActiveStatement(stmt.id)}>{stmt.label}</button>))}
      </div>
      {activeStatement === 'breakdown' ? (
        <div className="breakdown-section">
          <div className="breakdown-header">
            <h3>Revenue Breakdown</h3>
            <div className="breakdown-toggle">
              <button className={activeBreakdown === 'segment' ? 'active' : ''} onClick={() => setActiveBreakdown('segment')}>By Segment</button>
              <button className={activeBreakdown === 'geography' ? 'active' : ''} onClick={() => setActiveBreakdown('geography')}>By Geography</button>
            </div>
          </div>
          <div className="breakdown-content">
            <div className="breakdown-chart-placeholder"><PieChart size={48} /><p>Revenue Breakdown Chart</p></div>
            <div className="breakdown-table">
              <table><thead><tr><th>Segment</th><th>Revenue</th><th>% of Total</th></tr></thead>
                <tbody>
                  {(activeBreakdown === 'segment' ? financials?.revenueBreakdown?.bySegment : financials?.revenueBreakdown?.byGeography)?.map((item, idx) => (
                    <tr key={idx}><td>{item.segment || item.region}</td><td>{formatCurrency(item.revenue)}</td><td>{item.percentage.toFixed(2)}%</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="financial-table-container">
          <h3>{symbol} {activeStatement === 'income' ? 'Income Statement' : activeStatement === 'balance' ? 'Balance Sheet' : 'Cash Flow Statement'}</h3>
          <div className="table-scroll">
            <table className="financial-table">
              <thead><tr><th>Fiscal Period</th>{financials?.[activeStatement]?.annual?.map((period, idx) => (<th key={idx}>{period.period}</th>))}</tr></thead>
              <tbody>
                {activeStatement === 'income' && (<>
                  <tr><td>Period Ending</td>{financials?.income?.annual?.map((p, i) => <td key={i}>{p.endDate}</td>)}</tr>
                  <tr><td>Revenue</td>{financials?.income?.annual?.map((p, i) => <td key={i}>{formatCurrency(p.revenue)}</td>)}</tr>
                  <tr className="highlight-row"><td>Revenue Growth (YoY)</td>{financials?.income?.annual?.map((p, i) => (<td key={i} className={p.revenueGrowth >= 0 ? 'positive' : 'negative'}>{formatPercent(p.revenueGrowth)}</td>))}</tr>
                  <tr><td>Cost of Revenue</td>{financials?.income?.annual?.map((p, i) => <td key={i}>{formatCurrency(p.costOfRevenue)}</td>)}</tr>
                  <tr><td>Gross Profit</td>{financials?.income?.annual?.map((p, i) => <td key={i}>{formatCurrency(p.grossProfit)}</td>)}</tr>
                  <tr><td>Operating Expenses</td>{financials?.income?.annual?.map((p, i) => <td key={i}>{formatCurrency(p.operatingExpenses)}</td>)}</tr>
                  <tr><td>Operating Income</td>{financials?.income?.annual?.map((p, i) => <td key={i}>{formatCurrency(p.operatingIncome)}</td>)}</tr>
                  <tr className="highlight-row"><td>Net Income</td>{financials?.income?.annual?.map((p, i) => <td key={i}>{formatCurrency(p.netIncome)}</td>)}</tr>
                  <tr><td>EPS</td>{financials?.income?.annual?.map((p, i) => <td key={i}>${p.eps?.toFixed(2)}</td>)}</tr>
                </>)}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// FORECAST TAB
// =============================================================================
function ForecastTab({ symbol, analystData, onOpenAI }) {
  const { ratings, priceTargets, forecasts } = analystData || {};
  const totalRatings = (ratings?.buy || 0) + (ratings?.hold || 0) + (ratings?.sell || 0);
  const aiInsight = `With 20 out of 21 analysts rating ${symbol} as a Buy, Wall Street sentiment is overwhelmingly positive. The median price target of $625 implies 45% upside from current levels. Recent target revisions have been mixed, with some analysts trimming targets after the recent pullback.`;

  return (
    <div className="tab-content forecast-tab">
      <AIInsightCard title="Analyst Outlook" insight={aiInsight} onAskMore={() => onOpenAI?.(`What are analysts saying about ${symbol}?`)} />
      <div className="forecast-grid">
        <div className="forecast-card ratings-card">
          <h3><BarChart3 size={18} /> Analyst Ratings</h3>
          <div className="ratings-donut">
            <div className="donut-chart-placeholder"><div className="consensus-label">{ratings?.consensus || 'Buy'}</div></div>
            <div className="ratings-legend">
              <div className="legend-item buy"><span className="legend-color"></span><span>Buy</span><span className="legend-value">{ratings?.buy || 0}</span></div>
              <div className="legend-item hold"><span className="legend-color"></span><span>Neutral</span><span className="legend-value">{ratings?.hold || 0}</span></div>
              <div className="legend-item sell"><span className="legend-color"></span><span>Sell</span><span className="legend-value">{ratings?.sell || 0}</span></div>
            </div>
          </div>
          <p className="ratings-note">Based on {totalRatings} analyst ratings in the last 6 months.</p>
        </div>
        <div className="forecast-card targets-card">
          <h3><Target size={18} /> Price Targets</h3>
          <div className="target-main">
            <span className="target-price">{formatCurrency(priceTargets?.median, false)}</span>
            <span className={`target-upside ${(priceTargets?.upside || 0) >= 0 ? 'positive' : 'negative'}`}>(+{priceTargets?.upside?.toFixed(2)}% Upside)</span>
          </div>
          <p className="target-note">Based on {totalRatings} Wall Street analysts offering price targets for {symbol} in the last 6 months. The median price target is {formatCurrency(priceTargets?.median, false)} with a high estimate of {formatCurrency(priceTargets?.high, false)} and a low estimate of {formatCurrency(priceTargets?.low, false)}.</p>
          <div className="target-range">
            <div className="range-item high"><span className="range-value">{formatCurrency(priceTargets?.high, false)}</span><span className="range-label">Highest Target</span></div>
            <div className="range-item median"><span className="range-value">{formatCurrency(priceTargets?.median, false)}</span><span className="range-label">Median Target</span></div>
            <div className="range-item low"><span className="range-value">{formatCurrency(priceTargets?.low, false)}</span><span className="range-label">Lowest Target</span></div>
          </div>
        </div>
      </div>
      <div className="forecasts-table-section">
        <h3>{symbol} Analyst Forecasts</h3>
        <div className="table-scroll">
          <table className="forecasts-table">
            <thead><tr><th>Analyst</th><th>Firm</th><th>Current</th><th>Previous</th><th>Date</th></tr></thead>
            <tbody>{forecasts?.map((f, idx) => (<tr key={idx}><td>{f.analyst}</td><td>{f.firm}</td><td className={f.priceTarget >= f.previousTarget ? 'positive' : 'negative'}>{formatCurrency(f.priceTarget, false)}</td><td>{formatCurrency(f.previousTarget, false)}</td><td>{f.date}</td></tr>))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// INSIDERS TAB
// =============================================================================
function InsidersTab({ symbol, insiderData, quote, onOpenAI }) {
  const { summary, transactions } = insiderData || {};
  const aiInsight = `Insider activity for ${symbol} shows net selling of ${formatNumber(Math.abs(summary?.netShares || 0), { compact: true })} shares over the past 6 months. CFO Amy Hood sold $6.4M worth of shares in October, while CEO Satya Nadella sold $4.1M in September. This selling pattern is consistent with pre-planned 10b5-1 trading plans rather than signaling concerns about company outlook.`;

  return (
    <div className="tab-content insiders-tab">
      <AIInsightCard title="Insider Activity Analysis" insight={aiInsight} onAskMore={() => onOpenAI?.(`Explain the insider trading activity for ${symbol}`)} />
      <div className="insider-chart-section">
        <h3>{symbol} Stock Insider Trading Activity</h3>
        <div className="chart-legend">
          <span className="legend-item"><span className="dot closing"></span> Closing Price</span>
          <span className="legend-item"><span className="dot purchase"></span> Purchase</span>
          <span className="legend-item"><span className="dot sale"></span> Sale</span>
        </div>
        <div className="chart-placeholder insider-chart"><LineChart size={48} /><p>Insider Trading Chart with Price Overlay</p></div>
      </div>
      <div className="insider-table-section">
        <h3>Recent Insider Transactions</h3>
        <div className="table-scroll">
          <table className="insider-table">
            <thead><tr><th>Name</th><th>Type</th><th>Shares</th><th>Price</th><th>Shares Held</th><th>Date</th><th>Reported</th></tr></thead>
            <tbody>
              {transactions?.map((t, idx) => (
                <tr key={idx}>
                  <td><div className="insider-name">{t.name}</div><div className="insider-title">{t.title}</div></td>
                  <td className={t.type === 'Sale' ? 'negative' : 'positive'}>{t.type}</td>
                  <td>{formatNumber(t.shares, { decimals: 0 })}</td>
                  <td>{formatCurrency(t.price, false)}</td>
                  <td>{formatNumber(t.sharesHeld, { decimals: 0 })}</td>
                  <td>{t.date}</td><td>{t.reported}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// OWNERSHIP TREEMAP
// =============================================================================
const getHolderColor = (index, change) => {
  if (change > 2) return '#10b981'; if (change > 0) return '#34d399';
  if (change < -2) return '#ef4444'; if (change < 0) return '#f87171';
  return '#6366f1';
};

function OwnershipTreemap({ data, symbol }) {
  const treemapData = useMemo(() => {
    if (!data?.topHolders) return [];
    const totalShares = data.topHolders.reduce((sum, h) => sum + h.shares, 0);
    return [{ name: 'Institutional Ownership', children: data.topHolders.map((holder, idx) => ({
      name: holder.name.length > 20 ? holder.name.substring(0, 18) + '...' : holder.name,
      fullName: holder.name, size: holder.shares, value: holder.value, change: holder.changePercent,
      percentage: ((holder.shares / totalShares) * 100).toFixed(1), fill: getHolderColor(idx, holder.changePercent)
    })) }];
  }, [data]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0].payload;
    return (<div className="treemap-tooltip"><div className="tooltip-name">{item.fullName}</div><div className="tooltip-row"><span>Shares:</span><span>{formatNumber(item.size, { compact: true })}</span></div><div className="tooltip-row"><span>Value:</span><span>{formatCurrency(item.value, true)}</span></div><div className="tooltip-row"><span>% of Total:</span><span>{item.percentage}%</span></div><div className={`tooltip-row ${item.change >= 0 ? 'positive' : 'negative'}`}><span>QoQ Change:</span><span>{item.change >= 0 ? '+' : ''}{item.change?.toFixed(2)}%</span></div></div>);
  };

  const CustomContent = (props) => {
    const { x, y, width, height, name, percentage, change, fill } = props;
    if (width < 50 || height < 30) return null;
    return (<g><rect x={x} y={y} width={width} height={height} style={{ fill, stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1, cursor: 'pointer' }} />
      {width > 80 && height > 50 && (<><text x={x + width / 2} y={y + height / 2 - 8} textAnchor="middle" fill="white" fontSize={width > 120 ? 12 : 10} fontWeight="600">{name}</text><text x={x + width / 2} y={y + height / 2 + 8} textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize={10}>{percentage}%</text>{height > 70 && <text x={x + width / 2} y={y + height / 2 + 22} textAnchor="middle" fill={change >= 0 ? '#10b981' : '#ef4444'} fontSize={9}>{change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(1)}%</text>}</>)}
    </g>);
  };

  return (<div className="ownership-treemap"><ResponsiveContainer width="100%" height={350}><Treemap data={treemapData} dataKey="size" aspectRatio={4/3} stroke="rgba(255,255,255,0.1)" content={<CustomContent />}><Tooltip content={<CustomTooltip />} /></Treemap></ResponsiveContainer><div className="treemap-legend"><div className="legend-item"><span className="legend-color buying"></span><span>Increasing Position</span></div><div className="legend-item"><span className="legend-color selling"></span><span>Decreasing Position</span></div><div className="legend-item"><span className="legend-color neutral"></span><span>Unchanged</span></div></div></div>);
}

// =============================================================================
// INSTITUTIONS TAB
// =============================================================================
function InstitutionsTab({ symbol, institutionalData, onOpenAI }) {
  const { summary, topHolders, etfHolders } = institutionalData || {};
  const aiInsight = `Institutional ownership of ${symbol} remains strong at ${summary?.percentHeld}%. Vanguard leads with 717.9M shares (+2.27% QoQ), followed by BlackRock with 591.9M shares. The increased/decreased ratio of ${summary?.increased}/${summary?.decreased} suggests net accumulation by institutions this quarter.`;

  return (
    <div className="tab-content institutions-tab">
      <AIInsightCard title="Institutional Ownership Analysis" insight={aiInsight} onAskMore={() => onOpenAI?.(`Who are the major institutional holders of ${symbol}?`)} />
      <div className="ownership-treemap-section"><h3>{symbol} Stock Institutional Owners</h3><OwnershipTreemap data={institutionalData} symbol={symbol} /></div>
      <div className="holders-table-section">
        <h3>Top Institutional Holders</h3>
        <div className="table-scroll">
          <table className="holders-table">
            <thead><tr><th>Investor</th><th>Shares</th><th>Change in Shares</th><th>Market Value</th><th>Date</th><th>Reported</th></tr></thead>
            <tbody>{topHolders?.map((h, idx) => (<tr key={idx}><td><div className="holder-name">{h.name}</div><div className="holder-type">{h.type}</div></td><td>{formatNumber(h.shares, { decimals: 0 })}</td><td className={h.change >= 0 ? 'positive' : 'negative'}>{h.change >= 0 ? '+' : ''}{formatNumber(h.change, { decimals: 0 })}<span className="change-percent">{h.change >= 0 ? '+' : ''}{h.changePercent?.toFixed(2)}%</span></td><td>{formatCurrency(h.value)}</td><td>{h.date}</td><td>—</td></tr>))}</tbody>
          </table>
        </div>
      </div>
      <div className="etf-holders-section">
        <h3>Top ETF Holders</h3>
        <div className="table-scroll">
          <table className="etf-table"><thead><tr><th>ETF</th><th>Est. Holding Size</th></tr></thead>
            <tbody>{etfHolders?.map((e, idx) => (<tr key={idx}><td><div className="etf-symbol">{e.name}</div><div className="etf-name">{e.fullName}</div></td><td>{formatCurrency(e.holdings)}</td></tr>))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// GOVERNMENT TAB
// =============================================================================
function GovernmentTab({ symbol, congressData, onOpenAI }) {
  const { trades, lobbying, contracts } = congressData || {};
  const aiInsight = `${symbol} has seen 12 congressional trades in the past year, with 8 purchases and 4 sales. Senator Markwayne Mullin made the largest purchase ($100K-$250K) in December. The company spent $2.42M on lobbying in Q1 2026 covering AI policy, taxation, and trade issues. Government contracts totaled $4.3M this quarter, primarily with DoT and SSA.`;

  return (
    <div className="tab-content government-tab">
      <AIInsightCard title="Government Relations Analysis" insight={aiInsight} onAskMore={() => onOpenAI?.(`What's the relationship between ${symbol} and the government?`)} />
      <div className="government-grid">
        <div className="government-card">
          <div className="card-header"><h3><Landmark size={18} /> Congress Trading</h3></div>
          <p className="card-subtitle">Recent trades of {symbol} by members of U.S. Congress</p>
          <div className="table-scroll">
            <table className="congress-table"><thead><tr><th>Politician</th><th>Type</th><th>Traded</th></tr></thead>
              <tbody>{trades?.map((t, idx) => (<tr key={idx}><td><div className="politician-name">{t.politician}</div><div className="politician-info">{t.chamber} / {t.party}</div></td><td className={t.type === 'Purchase' ? 'positive' : 'negative'}>{t.type}</td><td><div>{t.amount}</div><div className="trade-date">{t.date}</div></td></tr>))}</tbody>
            </table>
          </div>
        </div>
        <div className="government-card">
          <div className="card-header"><h3><Building2 size={18} /> Corporate Lobbying</h3></div>
          <p className="card-subtitle">Government lobbying spending instances</p>
          <div className="lobbying-list">{lobbying?.map((l, idx) => (<div key={idx} className="lobbying-item"><div className="lobbying-header"><span className="lobbying-amount">{formatCurrency(l.amount, false)}</span><span className="lobbying-date">{l.date}</span></div><div className="lobbying-issues"><span className="label">Issue:</span><p>{l.issues}</p></div></div>))}</div>
        </div>
      </div>
      <div className="lobbying-chart-section"><h3>{symbol} Estimated Quarterly Lobbying Spending</h3><div className="chart-placeholder"><BarChart3 size={48} /><p>Quarterly Lobbying Chart</p></div></div>
      <div className="contracts-section">
        <h3><FileText size={18} /> Government Contracts</h3>
        <p className="card-subtitle">Federal grants, loans, and purchases</p>
        <div className="table-scroll">
          <table className="contracts-table"><thead><tr><th>Amount</th><th>Agency</th><th>Date</th></tr></thead>
            <tbody>{contracts?.map((c, idx) => (<tr key={idx}><td className="contract-amount">{formatCurrency(c.amount, false)}</td><td>{c.agency}</td><td>{c.date}</td></tr>))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// OWNERSHIP TAB
// =============================================================================
function OwnershipTab({ symbol, institutionalData, onOpenAI }) {
  const { topHolders } = institutionalData || {};
  const aiInsight = `Top shareholders of ${symbol} are dominated by index fund managers. Vanguard Group holds 9.6% of outstanding shares, followed by BlackRock at 7.9%. The top 10 holders control approximately 35% of total shares outstanding.`;

  return (
    <div className="tab-content ownership-tab">
      <AIInsightCard title="Ownership Structure" insight={aiInsight} onAskMore={() => onOpenAI?.(`Break down the ownership structure of ${symbol}`)} />
      <div className="ownership-table-section">
        <h3>{symbol} Top Shareholders</h3>
        <div className="table-scroll">
          <table className="ownership-table"><thead><tr><th>Shareholder</th><th>Shares Held</th><th></th></tr></thead>
            <tbody>{topHolders?.map((h, idx) => (<tr key={idx}><td><div className="shareholder-name">{h.name}</div><div className="shareholder-type">{h.type}</div></td><td>{formatNumber(h.shares, { decimals: 0 })}</td><td><button className="track-btn">Track</button></td></tr>))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// NEWS TAB
// =============================================================================
function NewsTab({ symbol, news, onOpenAI }) {
  const aiInsight = `Recent news sentiment for ${symbol} is predominantly positive, with 3 bullish articles, 1 bearish, and 1 neutral in the past week. Key themes include AI partnerships, analyst upgrades, and institutional buying activity.`;

  return (
    <div className="tab-content news-tab">
      <AIInsightCard title="News Sentiment" insight={aiInsight} onAskMore={() => onOpenAI?.(`Summarize the latest news for ${symbol}`)} />
      <div className="news-list">
        {news?.map((article) => (
          <article key={article.id} className="news-article">
            <div className="news-content">
              <div className="news-meta"><span className="news-source">{article.source}</span><span className="news-time"><Clock size={12} /> {article.time}</span></div>
              <h4 className="news-title">{article.title}</h4>
              <span className={`sentiment-badge ${article.sentiment}`}>
                {article.sentiment === 'bullish' && <TrendingUp size={12} />}
                {article.sentiment === 'bearish' && <TrendingDown size={12} />}
                {article.sentiment === 'neutral' && <Minus size={12} />}
                {article.sentiment}
              </span>
            </div>
            {article.image && (<img src={article.image} alt="" className="news-image" />)}
          </article>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// COMPARISON MODE COMPONENT
// =============================================================================
function ComparisonMode({ currentSymbol, onClose }) {
  const [compareSymbols, setCompareSymbols] = useState([currentSymbol]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [compareData, setCompareData] = useState({});
  const [activeMetric, setActiveMetric] = useState('overview');

  const metrics = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'valuation', label: 'Valuation', icon: DollarSign },
    { id: 'performance', label: 'Performance', icon: TrendingUp },
    { id: 'financials', label: 'Financials', icon: FileText },
  ];

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.length < 1) { setSearchResults([]); return; }
    const mockResults = [
      { symbol: 'AAPL', name: 'Apple Inc.' },{ symbol: 'GOOGL', name: 'Alphabet Inc.' },
      { symbol: 'AMZN', name: 'Amazon.com Inc.' },{ symbol: 'META', name: 'Meta Platforms' },
      { symbol: 'NVDA', name: 'NVIDIA Corp.' },{ symbol: 'TSLA', name: 'Tesla Inc.' },
      { symbol: 'MSFT', name: 'Microsoft Corp.' },
    ].filter(s => s.symbol.toLowerCase().includes(query.toLowerCase()) || s.name.toLowerCase().includes(query.toLowerCase()));
    setSearchResults(mockResults);
  };

  const addSymbol = (symbol) => {
    if (compareSymbols.length < 5 && !compareSymbols.includes(symbol)) {
      setCompareSymbols([...compareSymbols, symbol]);
      setSearchQuery(''); setSearchResults([]);
      loadCompareData(symbol);
    }
  };

  const removeSymbol = (symbol) => { if (symbol !== currentSymbol) setCompareSymbols(compareSymbols.filter(s => s !== symbol)); };

  const loadCompareData = (sym) => {
    setCompareData(prev => ({ ...prev, [sym]: {
      quote: { price: 150 + Math.random() * 100, changePercent: (Math.random() - 0.5) * 5 },
      metrics: { marketCap: (Math.random() * 3 + 1) * 1e12, peRatio: Math.random() * 30 + 15, forwardPE: Math.random() * 25 + 12, priceToSales: Math.random() * 10 + 2, revenue: (Math.random() * 300 + 100) * 1e9, revenueGrowth: (Math.random() - 0.3) * 30, netIncome: (Math.random() * 80 + 20) * 1e9, profitMargin: Math.random() * 30 + 10, ytdReturn: (Math.random() - 0.3) * 50, oneYearReturn: (Math.random() - 0.2) * 80, beta: Math.random() * 1.5 + 0.5 }
    }}));
  };

  useEffect(() => { loadCompareData(currentSymbol); }, [currentSymbol]);

  const metricRows = {
    overview: [{ label: 'Price', key: 'price', format: 'currency' },{ label: 'Change %', key: 'changePercent', format: 'percent', highlight: true },{ label: 'Market Cap', key: 'marketCap', format: 'currency-compact' },{ label: 'P/E Ratio', key: 'peRatio', format: 'number' },{ label: 'Beta', key: 'beta', format: 'number' }],
    valuation: [{ label: 'P/E Ratio', key: 'peRatio', format: 'number' },{ label: 'Forward P/E', key: 'forwardPE', format: 'number' },{ label: 'P/S Ratio', key: 'priceToSales', format: 'number' }],
    performance: [{ label: 'YTD Return', key: 'ytdReturn', format: 'percent', highlight: true },{ label: '1Y Return', key: 'oneYearReturn', format: 'percent', highlight: true },{ label: 'Beta', key: 'beta', format: 'number' }],
    financials: [{ label: 'Revenue', key: 'revenue', format: 'currency-compact' },{ label: 'Revenue Growth', key: 'revenueGrowth', format: 'percent', highlight: true },{ label: 'Net Income', key: 'netIncome', format: 'currency-compact' },{ label: 'Profit Margin', key: 'profitMargin', format: 'percent' }],
  };

  const formatVal = (value, format, highlight) => {
    if (value === undefined) return '—';
    switch (format) { case 'currency': return `$${value.toFixed(2)}`; case 'currency-compact': return formatCurrency(value, true); case 'percent': return `${highlight && value >= 0 ? '+' : ''}${value.toFixed(2)}%`; default: return value.toFixed(2); }
  };

  return (
    <div className="comparison-mode-overlay" onClick={onClose}>
      <div className="comparison-mode-modal" onClick={e => e.stopPropagation()}>
        <div className="comparison-header"><div className="comparison-title"><GitCompare size={20} /><h2>Compare Stocks</h2></div><button className="comparison-close" onClick={onClose}><X size={20} /></button></div>
        <div className="comparison-add-section">
          <div className="compare-search-wrapper"><Search size={16} /><input type="text" placeholder="Add symbol to compare (max 5)..." value={searchQuery} onChange={(e) => handleSearch(e.target.value)} disabled={compareSymbols.length >= 5} /></div>
          {searchResults.length > 0 && (<div className="compare-search-results">{searchResults.map(r => (<button key={r.symbol} className="compare-search-item" onClick={() => addSymbol(r.symbol)} disabled={compareSymbols.includes(r.symbol)}><span className="search-symbol">{r.symbol}</span><span className="search-name">{r.name}</span></button>))}</div>)}
          <div className="compare-selected">{compareSymbols.map(sym => (<div key={sym} className={`compare-chip ${sym === currentSymbol ? 'primary' : ''}`}><span>{sym}</span>{sym !== currentSymbol && <button onClick={() => removeSymbol(sym)}><X size={12} /></button>}</div>))}</div>
        </div>
        <div className="comparison-metrics-tabs">{metrics.map(m => (<button key={m.id} className={`metric-tab ${activeMetric === m.id ? 'active' : ''}`} onClick={() => setActiveMetric(m.id)}><m.icon size={14} />{m.label}</button>))}</div>
        <div className="comparison-content">
          <div className="comparison-table">
            <table>
              <thead><tr><th>Metric</th>{compareSymbols.map(sym => (<th key={sym}><div className="compare-header-cell"><span>{sym}</span>{sym !== currentSymbol && <button className="remove-compare-btn" onClick={() => removeSymbol(sym)}><X size={12} /></button>}</div></th>))}</tr></thead>
              <tbody>{(metricRows[activeMetric] || []).map(row => (<tr key={row.key}><td className="metric-label">{row.label}</td>{compareSymbols.map(sym => { const d = compareData[sym]; const val = row.key === 'price' || row.key === 'changePercent' ? d?.quote?.[row.key] : d?.metrics?.[row.key]; return <td key={sym} className={row.highlight && val !== undefined ? (val >= 0 ? 'positive' : 'negative') : ''}>{formatVal(val, row.format, row.highlight)}</td>; })}</tr>))}</tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// TIMELINE VIEW COMPONENT
// =============================================================================
function TimelineView({ symbol, insiderData, congressData, news, onOpenAI }) {
  const [filter, setFilter] = useState('all');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const timelineRef = useRef(null);

  const timelineEvents = useMemo(() => {
    const events = [];
    insiderData?.transactions?.forEach(t => events.push({ type: 'insider', date: new Date(t.date || '2026-01-15'), title: `${t.name} ${t.type}`, subtitle: t.title, detail: `${t.shares?.toLocaleString()} shares @ $${t.price?.toFixed(2)}`, sentiment: t.type?.toLowerCase().includes('buy') ? 'bullish' : 'bearish', icon: Users }));
    congressData?.trades?.forEach(t => events.push({ type: 'congress', date: new Date(t.date || '2026-01-10'), title: `${t.politician} ${t.type}`, subtitle: `${t.chamber} (${t.party})`, detail: t.amount, sentiment: t.type?.toLowerCase() === 'purchase' ? 'bullish' : 'bearish', icon: Landmark }));
    congressData?.contracts?.forEach(c => events.push({ type: 'contract', date: new Date(c.date || '2026-01-16'), title: 'Government Contract', subtitle: c.agency, detail: formatCurrency(c.amount), sentiment: 'bullish', icon: FileText }));
    news?.forEach(n => { const d = new Date(); d.setHours(d.getHours() - (parseInt(n.time) || 24)); events.push({ type: 'news', date: d, title: n.title, subtitle: n.source, sentiment: n.sentiment, icon: FileText }); });
    return events.sort((a, b) => b.date - a.date);
  }, [insiderData, congressData, news]);

  const filteredEvents = useMemo(() => filter === 'all' ? timelineEvents : timelineEvents.filter(e => e.type === filter), [timelineEvents, filter]);

  useEffect(() => {
    if (isPlaying && currentIndex < filteredEvents.length - 1) { const timer = setTimeout(() => setCurrentIndex(prev => prev + 1), 2000); return () => clearTimeout(timer); }
    else if (currentIndex >= filteredEvents.length - 1) setIsPlaying(false);
  }, [isPlaying, currentIndex, filteredEvents.length]);

  const filterOptions = [{ id: 'all', label: 'All', icon: Activity },{ id: 'insider', label: 'Insider', icon: Users },{ id: 'congress', label: 'Congress', icon: Landmark },{ id: 'contract', label: 'Contracts', icon: FileText },{ id: 'news', label: 'News', icon: FileText }];

  const formatDate = (date) => { const days = Math.floor((new Date() - date) / (1000 * 60 * 60 * 24)); if (days === 0) return 'Today'; if (days === 1) return 'Yesterday'; if (days < 7) return `${days} days ago`; return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };

  return (
    <div className="timeline-view">
      <AIInsightCard title="Timeline Analysis" insight={`${symbol} has had ${timelineEvents.length} significant events recently including ${insiderData?.transactions?.length || 0} insider transactions and ${congressData?.trades?.length || 0} congressional trades.`} onAskMore={() => onOpenAI?.(`Summarize recent events for ${symbol}`)} />
      <div className="timeline-controls">
        <div className="timeline-filters">{filterOptions.map(opt => (<button key={opt.id} className={`timeline-filter-btn ${filter === opt.id ? 'active' : ''}`} onClick={() => { setFilter(opt.id); setCurrentIndex(0); }}><opt.icon size={14} />{opt.label}</button>))}</div>
        <div className="timeline-playback">
          <button className="playback-btn" onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0}><ChevronLeft size={16} /></button>
          <button className="playback-btn play" onClick={() => setIsPlaying(!isPlaying)}>{isPlaying ? <Pause size={16} /> : <Play size={16} />}</button>
          <button className="playback-btn" onClick={() => setCurrentIndex(Math.min(filteredEvents.length - 1, currentIndex + 1))} disabled={currentIndex >= filteredEvents.length - 1}><ChevronRight size={16} /></button>
          <span className="playback-counter">{currentIndex + 1} / {filteredEvents.length}</span>
        </div>
      </div>
      <div className="timeline-container" ref={timelineRef}>
        {filteredEvents.map((event, idx) => { const Icon = event.icon; return (
          <div key={idx} className={`timeline-item ${event.sentiment} ${idx === currentIndex && isPlaying ? 'active' : ''}`}>
            <div className="timeline-marker"><div className={`marker-dot ${event.sentiment}`}><Icon size={12} /></div>{idx < filteredEvents.length - 1 && <div className="marker-line" />}</div>
            <div className="timeline-content"><div className="timeline-date">{formatDate(event.date)}</div><div className="timeline-card"><div className="timeline-card-header"><span className={`event-type ${event.type}`}>{event.type}</span><span className={`sentiment-dot ${event.sentiment}`}></span></div><h4 className="timeline-title">{event.title}</h4>{event.subtitle && <p className="timeline-subtitle">{event.subtitle}</p>}{event.detail && <p className="timeline-detail">{event.detail}</p>}</div></div>
          </div>
        ); })}
        {filteredEvents.length === 0 && <div className="timeline-empty"><Clock size={32} /><p>No events found</p></div>}
      </div>
    </div>
  );
}

// =============================================================================
// MAIN STOCK PAGE COMPONENT
// =============================================================================
function StockPage() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isWatchlisted, setIsWatchlisted] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);

  const [quote, setQuote] = useState(null);
  const [profile, setProfile] = useState(null);
  const [keyStats, setKeyStats] = useState(null);
  const [financials, setFinancials] = useState(null);
  const [analystData, setAnalystData] = useState(null);
  const [insiderData, setInsiderData] = useState(null);
  const [institutionalData, setInstitutionalData] = useState(null);
  const [congressData, setCongressData] = useState(null);
  const [news, setNews] = useState(null);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: null },
    { id: 'financials', label: 'Financials', icon: null },
    { id: 'forecast', label: 'Forecast', icon: null },
    { id: 'insiders', label: 'Insiders', icon: null },
    { id: 'institutions', label: 'Institutions', icon: null },
    { id: 'government', label: 'Government', icon: null },
    { id: 'ownership', label: 'Ownership', icon: null },
    { id: 'timeline', label: 'Timeline', icon: Clock },
    { id: 'news', label: 'News', icon: null }
  ];

  useEffect(() => {
    if (!symbol) return;
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const mockData = getMockData(symbol.toUpperCase());
        setQuote(mockData.quote);
        setProfile(mockData.profile);
        setKeyStats(mockData.keyStats);
        setFinancials(getMockFinancials(symbol));
        setAnalystData(getMockAnalystData(symbol));
        setInsiderData(getMockInsiderData(symbol));
        setInstitutionalData(getMockInstitutionalData(symbol));
        setCongressData(getMockCongressData(symbol));
        setNews(getMockNews(symbol));
        const watchlist = JSON.parse(localStorage.getItem('watchlist') || '[]');
        setIsWatchlisted(watchlist.includes(symbol.toUpperCase()));
      } catch (err) {
        console.error('Error fetching stock data:', err);
        setError('Failed to load stock data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [symbol]);

  useEffect(() => {
    setSearchParams({ tab: activeTab }, { replace: true });
  }, [activeTab, setSearchParams]);

  const handleTabChange = (tabId) => setActiveTab(tabId);

  const handleAddToWatchlist = () => {
    const watchlist = JSON.parse(localStorage.getItem('watchlist') || '[]');
    if (isWatchlisted) {
      const updated = watchlist.filter(s => s !== symbol.toUpperCase());
      localStorage.setItem('watchlist', JSON.stringify(updated));
    } else {
      watchlist.push(symbol.toUpperCase());
      localStorage.setItem('watchlist', JSON.stringify(watchlist));
    }
    setIsWatchlisted(!isWatchlisted);
  };

  const handleSetAlert = () => setShowAlertModal(true);
  const handleAlertSave = (alert) => console.log('Alert created:', alert);

  // Opens the site-wide AIChatWidget via custom event instead of a page-specific modal
  const handleOpenAI = (prompt) => {
    window.dispatchEvent(new CustomEvent('open-ai-chat', { detail: { prompt } }));
  };

  const handleBack = () => navigate(-1);

  if (isLoading) return <div className="stock-page loading"><LoadingSpinner /></div>;
  if (error) return <div className="stock-page error"><ErrorState message={error} onRetry={() => window.location.reload()} /></div>;

  return (
    <div className="stock-page">
      <button className="back-btn" onClick={handleBack}><ArrowLeft size={18} /> Back</button>

      <PriceHeader quote={quote} profile={profile} onAddToWatchlist={handleAddToWatchlist} onSetAlert={handleSetAlert} onCompare={() => setShowCompareModal(true)} isWatchlisted={isWatchlisted} />

      <div className="chart-section">
        <div className="chart-header">
          <h3>Price Chart</h3>
          <div className="chart-controls">
            {['1D', '1W', '1M', '3M', '1Y', '5Y'].map(range => (<button key={range} className="chart-range-btn">{range}</button>))}
          </div>
        </div>
        <div className="chart-placeholder" style={{ position: 'relative' }}>
          <div className="tradingview-widget-placeholder"><LineChart size={48} /><p>TradingView Chart</p><span>Interactive chart will load here</span></div>
          <button
            className="open-full-chart-btn"
            onClick={() => navigate(`/chart/${symbol}`)}
            style={{
              position: 'absolute',
              bottom: '14px',
              right: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '0.78rem',
              fontWeight: 600,
              cursor: 'pointer',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.25), rgba(102, 126, 234, 0.25))',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              color: '#c4b5fd',
              transition: 'all 0.2s',
              zIndex: 2,
              backdropFilter: 'blur(8px)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.4), rgba(102, 126, 234, 0.4))'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.25), rgba(102, 126, 234, 0.25))'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <Maximize2 size={14} /> Full Chart
          </button>
        </div>
      </div>

      <TabNavigation activeTab={activeTab} onTabChange={handleTabChange} tabs={tabs} />

      <div className="tab-content-container">
        {activeTab === 'overview' && <OverviewTab symbol={symbol} quote={quote} profile={profile} keyStats={keyStats} onOpenAI={handleOpenAI} />}
        {activeTab === 'financials' && <FinancialsTab symbol={symbol} financials={financials} onOpenAI={handleOpenAI} />}
        {activeTab === 'forecast' && <ForecastTab symbol={symbol} analystData={analystData} onOpenAI={handleOpenAI} />}
        {activeTab === 'insiders' && <InsidersTab symbol={symbol} insiderData={insiderData} quote={quote} onOpenAI={handleOpenAI} />}
        {activeTab === 'institutions' && <InstitutionsTab symbol={symbol} institutionalData={institutionalData} onOpenAI={handleOpenAI} />}
        {activeTab === 'government' && <GovernmentTab symbol={symbol} congressData={congressData} onOpenAI={handleOpenAI} />}
        {activeTab === 'ownership' && <OwnershipTab symbol={symbol} institutionalData={institutionalData} onOpenAI={handleOpenAI} />}
        {activeTab === 'timeline' && <TimelineView symbol={symbol} insiderData={insiderData} congressData={congressData} news={news} onOpenAI={handleOpenAI} />}
        {activeTab === 'news' && <NewsTab symbol={symbol} news={news} onOpenAI={handleOpenAI} />}
      </div>

      {showAlertModal && <AlertModal symbol={symbol} currentPrice={quote?.price} onClose={() => setShowAlertModal(false)} onSave={handleAlertSave} />}
      {showCompareModal && <ComparisonMode currentSymbol={symbol} onClose={() => setShowCompareModal(false)} />}
    </div>
  );
}

export default StockPage;